import type { ApiClient } from '@/helpers/apiClient'
import {
  apiKeyDtoSchema,
  apiKeyListDtoSchema,
  createdApiKeyDtoSchema,
  modeSettingsDtoSchema,
  tenantProfileDtoSchema,
  tenantSettingsDtoSchema,
  goLiveChecklistDtoSchema,
  type GoLiveChecklistDto,
  webhookSecretDtoSchema,
  type ApiKeyDto,
  type ApiKeyListDto,
  type CreatedApiKeyDto,
  type ModeSettingsDto,
  type TenantProfileDto,
  type TenantSettingsDto,
  type WebhookSecretDto,
} from '@lib/dto/tenant-settings.dto'
import type { TenantMode } from '@lib/enums/tenant-mode.enum'
import type {
  CreateApiKeyInput,
  RotateWebhookSecretInput,
  UpdateModeSettingsInput,
} from '@lib/input/tenant-settings.input'
import type { TenantProfileInput } from '@lib/input/tenant.input'
import type { Uuid } from '@lib/validators/params'
import type { ITenantSettingsService } from '@/types/TenantSettingsService.types'

function tenantPath(tenantId: Uuid): string {
  return `/dashboard/tenants/${encodeURIComponent(tenantId)}`
}

/**
 * A tenant's keys, integration settings, and profile. Bound to a session's
 * client: the tenant user's own, or staff's when viewing as the tenant.
 */
export class TenantSettingsService implements ITenantSettingsService {
  constructor(private readonly client: ApiClient) {}

  listKeys(tenantId: Uuid): Promise<ApiKeyListDto> {
    return this.client.get(`${tenantPath(tenantId)}/api-keys`, apiKeyListDtoSchema)
  }

  createKey(tenantId: Uuid, input: CreateApiKeyInput): Promise<CreatedApiKeyDto> {
    return this.client.post(`${tenantPath(tenantId)}/api-keys`, input, createdApiKeyDtoSchema)
  }

  revokeKey(tenantId: Uuid, keyId: Uuid): Promise<ApiKeyDto> {
    return this.client.post(
      `${tenantPath(tenantId)}/api-keys/${encodeURIComponent(keyId)}/revoke`,
      {},
      apiKeyDtoSchema,
    )
  }

  goLiveChecklist(tenantId: Uuid): Promise<GoLiveChecklistDto> {
    return this.client.get(`${tenantPath(tenantId)}/go-live`, goLiveChecklistDtoSchema)
  }

  getSettings(tenantId: Uuid): Promise<TenantSettingsDto> {
    return this.client.get(`${tenantPath(tenantId)}/settings`, tenantSettingsDtoSchema)
  }

  updateSettings(tenantId: Uuid, mode: TenantMode, input: UpdateModeSettingsInput): Promise<ModeSettingsDto> {
    return this.client.patch(`${tenantPath(tenantId)}/settings/${mode}`, input, modeSettingsDtoSchema)
  }

  webhookSecret(tenantId: Uuid, mode: TenantMode): Promise<WebhookSecretDto> {
    return this.client.get(`${tenantPath(tenantId)}/settings/${mode}/webhook-secret`, webhookSecretDtoSchema)
  }

  rotateWebhookSecret(tenantId: Uuid, mode: TenantMode, input: RotateWebhookSecretInput): Promise<WebhookSecretDto> {
    return this.client.post(
      `${tenantPath(tenantId)}/settings/${mode}/webhook-secret/rotate`,
      input,
      webhookSecretDtoSchema,
    )
  }

  retirePreviousWebhookSecret(tenantId: Uuid, mode: TenantMode): Promise<ModeSettingsDto> {
    return this.client.post(
      `${tenantPath(tenantId)}/settings/${mode}/webhook-secret/retire-previous`,
      {},
      modeSettingsDtoSchema,
    )
  }

  profile(tenantId: Uuid): Promise<TenantProfileDto> {
    return this.client.get(`${tenantPath(tenantId)}/profile`, tenantProfileDtoSchema)
  }

  updateProfile(tenantId: Uuid, input: TenantProfileInput): Promise<TenantProfileDto> {
    return this.client.patch(`${tenantPath(tenantId)}/profile`, input, tenantProfileDtoSchema)
  }
}
